import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { Collapse } from '@adornis/components/client/Collapse.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { NoPermission } from '../../permissions/client/NoPermission.js';
import { useMartinusABAC } from '../../permissions/client/useMartinusAbac.js';
import { UserEmployeeDetailsActions } from '../../users/employees/UserEmployeeDetailsPolicy.js';
import { Person } from '../../users/entities/person.js';
import type { UserOnUserPayload } from '../../users/UserPolicy.js';
import { LakAPI } from '../api/LakAPI.js';
import { LAKAdjustment } from '../entities/LAKAdjustment.js';
import { type LAKAdjustmentDialogValue, openLAKAdjustmentDialog } from './LAKAdjustmentDialog.js';
import { openPreviousLAKHoursDialog } from './PreviousLAKHoursDialog.js';

const formatDate = (date: Date | null | undefined) => (date ? formatCalendarDay(date, 'dd.MM.yyyy') : '—');

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * A period of lost weeks, from the Monday it opens to the Sunday of the week it closes with - the
 * whole weeks are what was lost, so showing the last Monday would cut six days off the answer.
 *
 * Canonical days are moved in milliseconds rather than with `addDays`, which keeps the *local* time
 * of day: a browser crossing a DST boundary within those six days would land an hour off 00:00 UTC
 * and render the neighbouring day.
 */
const formatWeekPeriod = (period: { start: Date; end: Date }) =>
  `${formatCalendarDay(period.start, 'dd.MM.')}–${formatCalendarDay(new Date(period.end.getTime() + 6 * DAY_MS), 'dd.MM.')}`;

const formatHours = (hours: Maybe<number>) =>
  (hours ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const roundHours = (hours: number) => Math.round(hours * 100) / 100;

/**
 * Manual Abstimmungen can take hours out, so a year can add a negative amount - the sign is part of the figure.
 */
const formatSignedHours = (hours: Maybe<number>) => `${(hours ?? 0) >= 0 ? '+' : ''}${formatHours(hours)}`;

/**
 * One calendar year of the timeline, as the yearly statement demands it: what the year earned and
 * which balance it closes with. Its segments are the intervalls cut at the turn of the year - they
 * stay cut along the employment and its absences, the calendar year is only laid over them.
 */
interface LAKYearRow {
  year: number;
  segments: LAKYearSegment[];
  hours: number;
  projectedHours: number;
  balance: number;
  projectedBalance: number;
}

interface LAKWeekWithoutCreditView {
  start: Maybe<Date>;
  absenceClasses: Maybe<readonly Maybe<string>[]>;
}

interface LAKYearSegment {
  contractID: Maybe<string>;
  supplementID: Maybe<string>;
  /**
   * The whole Abstimmung this segment is a year of, so it can be named and edited from any of its years.
   */
  adjustment: Maybe<LAKAdjustmentDialogValue>;
  start: Maybe<Date>;
  end: Maybe<Date>;
  lakHoursPerWeek: Maybe<number>;
  creditedWeeks: Maybe<number>;
  projectedCreditedWeeks: Maybe<number>;
  weeksWithoutCredit: Maybe<readonly LAKWeekWithoutCreditView[]>;
  workingHours: Maybe<number>;
  projectedWorkingHours: Maybe<number>;
}

/**
 * The lost weeks of a segment, grouped by what took them. Consecutive Mondays are folded into one
 * period, because "18.03. – 16.06." is what a year of Elternzeit actually looks like, while the
 * 13 dates it consists of are not readable.
 */
interface LAKAbsenceReason {
  absenceClass: string;
  weeks: number;
  periods: { start: Date; end: Date }[];
}

function collectAbsenceReasons(weeksWithoutCredit: readonly LAKWeekWithoutCreditView[]): LAKAbsenceReason[] {
  const mondaysByClass = new Map<string, Date[]>();

  for (const week of weeksWithoutCredit) {
    if (!week.start) continue;
    for (const absenceClass of week.absenceClasses ?? []) {
      if (!absenceClass) continue;
      const mondays = mondaysByClass.get(absenceClass) ?? [];
      mondays.push(new Date(week.start as unknown as Date));
      mondaysByClass.set(absenceClass, mondays);
    }
  }

  return [...mondaysByClass.entries()].map(([absenceClass, mondays]) => {
    mondays.sort((a, b) => a.getTime() - b.getTime());

    const periods: { start: Date; end: Date }[] = [];
    for (const monday of mondays) {
      const current = periods[periods.length - 1];
      // the Sunday of the week before this Monday closes the running period exactly one week earlier
      if (current && monday.getTime() - current.end.getTime() === WEEK_MS) current.end = monday;
      else periods.push({ start: monday, end: monday });
    }

    return { absenceClass, weeks: mondays.length, periods };
  });
}

interface LAKYearBearingIntervall {
  adjustmentID: Maybe<string>;
  adjustmentName: Maybe<string>;
  isOneTimeAdjustment: Maybe<boolean>;
  start: Maybe<Date>;
  end: Maybe<Date>;
  workingHoursInIntervall: Maybe<number>;
  projectedWorkingHoursInIntervall: Maybe<number>;
  lakHoursPerWeek: Maybe<number>;
  years: Maybe<readonly (Omit<LAKYearSegment, 'lakHoursPerWeek' | 'adjustment'> & { year: Maybe<number> })[]>;
}

/**
 * The hours per week live on the intervall, everything else on its years, so each segment is
 * collected together with the one piece of context its intervall holds. The balances run from the
 * oldest year on, because every year closes with what all years before it added up to.
 */
function collectYearRows(intervalls: readonly LAKYearBearingIntervall[], previousLAKHours: number): LAKYearRow[] {
  const segmentsByYear = new Map<number, LAKYearSegment[]>();

  for (const intervall of intervalls) {
    const adjustment: Maybe<LAKAdjustmentDialogValue> = intervall.adjustmentID
      ? {
          _id: intervall.adjustmentID,
          name: intervall.adjustmentName,
          start: intervall.start,
          end: intervall.isOneTimeAdjustment ? undefined : intervall.end,
          hoursPerWeek: intervall.isOneTimeAdjustment ? undefined : intervall.lakHoursPerWeek,
          hours: intervall.isOneTimeAdjustment
            ? (intervall.projectedWorkingHoursInIntervall ?? intervall.workingHoursInIntervall)
            : undefined,
        }
      : undefined;

    for (const year of intervall.years ?? []) {
      if (year.year == null) continue;
      const segments = segmentsByYear.get(year.year) ?? [];
      segments.push({
        contractID: year.contractID,
        supplementID: year.supplementID,
        adjustment,
        start: year.start,
        end: year.end,
        lakHoursPerWeek: intervall.lakHoursPerWeek,
        creditedWeeks: year.creditedWeeks,
        projectedCreditedWeeks: year.projectedCreditedWeeks,
        weeksWithoutCredit: year.weeksWithoutCredit,
        workingHours: year.workingHours,
        projectedWorkingHours: year.projectedWorkingHours,
      });
      segmentsByYear.set(year.year, segments);
    }
  }

  let balance = previousLAKHours;
  let projectedBalance = previousLAKHours;

  /**
   * Oldest year first, which is both the order the balances build up in and the order they are read
   * in: the Übertrag opens the list and every year below it closes with what all years above added up
   * to.
   */
  return [...segmentsByYear.entries()]
    .sort(([a], [b]) => a - b)
    .map(([year, segments]) => {
      const hours = segments.reduce((sum, segment) => sum + (segment.workingHours ?? 0), 0);
      const projectedHours = segments.reduce(
        (sum, segment) => sum + (segment.projectedWorkingHours ?? segment.workingHours ?? 0),
        0,
      );
      balance += hours;
      projectedBalance += projectedHours;

      segments.sort((a, b) => new Date(a.start ?? 0).getTime() - new Date(b.start ?? 0).getTime());

      return {
        year,
        segments,
        hours: roundHours(hours),
        projectedHours: roundHours(projectedHours),
        balance: roundHours(balance),
        projectedBalance: roundHours(projectedBalance),
      };
    });
}

export const LAKEmployeeOverview = stateful((props: { personID: Maybe<string> }) => {
  if (!props.personID) throw new Error('No user given');
  const user = useObservable(() => currentUser<Person>(), []);
  const [forceRecalculate, setForceRecalculate] = useState(0);
  const [openYears, setOpenYears] = useState<number[]>([new Date().getFullYear()]);

  const { value: person, loading: personLoading } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.personID,
      )({
        _id: 1,
        _class: 1,
        sorgeberechtigteKinderIDs: 1,
        spendenKinderIDs: 1,
        roles: { name: 1, contexts: 1, permissions: 1 },
        mitarbeiterDaten: { previousLAKHours: 1, previousLAKHoursYear: 1 },
      }),
    [props.personID],
  );

  const {
    can: canViewLak,
    loading: permissionLoading,
    reason,
  } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.VIEW_LAK,
    viewedUser: person,
  });

  const { can: canEditLak } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.EDIT_LAK,
    viewedUser: person,
  });

  const { value: lakIntervallResult, loading: lakIntervallResultLoading } = useQuery(
    () =>
      LakAPI.calculate(
        props.personID,
        forceRecalculate,
      )({
        _id: 1,
        _class: 1,
        createdAt: 1,
        retirementDate: 1,
        creditEndDate: 1,
        creditEndFollowsSchulhalbjahr: 1,
        creditEndBySchwerbehinderung: 1,
        creditStartDate: 1,
        previousLAKHoursYear: 1,
        intervalls: {
          _class: 1,
          isPreviousHours: 1,
          contractID: 1,
          supplementID: 1,
          adjustmentID: 1,
          adjustmentName: 1,
          isOneTimeAdjustment: 1,
          start: 1,
          end: 1,
          workingHoursPerWeek: 1,
          lakHoursPerWeek: 1,
          creditedWeeks: 1,
          projectedCreditedWeeks: 1,
          weeksWithoutCredit: { _class: 1, start: 1, absenceClasses: 1 },
          workingHoursInIntervall: 1,
          projectedWorkingHoursInIntervall: 1,
          years: {
            _class: 1,
            contractID: 1,
            supplementID: 1,
            adjustmentID: 1,
            year: 1,
            start: 1,
            end: 1,
            creditedWeeks: 1,
            projectedCreditedWeeks: 1,
            weeksWithoutCredit: { _class: 1, start: 1, absenceClasses: 1 },
            workingHours: 1,
            projectedWorkingHours: 1,
          },
        },
      }),
    [props.personID, forceRecalculate],
  );

  if (!user || personLoading || !person || lakIntervallResultLoading) return PageLoader({});

  if (!canViewLak && !permissionLoading) {
    return NoPermission({ message: reason });
  }

  // the calculation returns nothing for everybody without a LAK (`keepsLAK`)
  if (!lakIntervallResult) {
    return html`
      <div class="card-container">
        <div class="card card-body">
          <p>
            Ein Lebensarbeitszeitkonto wird nur für pädagogisches Personal geführt, das Hessischer Landesbeamter oder
            Angestellter ist.
          </p>
        </div>
      </div>
    `;
  }

  const previousLAKHours = person.mitarbeiterDaten?.previousLAKHours;
  const previousLAKHoursYear = lakIntervallResult?.previousLAKHoursYear;

  const editPreviousLAKHours = async () => {
    const { value, year } = await openPreviousLAKHoursDialog(
      previousLAKHours,
      person.mitarbeiterDaten?.previousLAKHoursYear,
    );
    await guardDialog(
      async () => {
        await LakAPI.setPreviousHours(props.personID, value ?? null, year)();
        /**
         * The calculation opens with these hours on the day after the Stichtag, so it has to run again.
         */
        setForceRecalculate(forceRecalculate + 1);
      },
      { successText: 'Übernommene LAK-Stunden gespeichert' },
    );
  };

  const editAdjustment = async (adjustment?: LAKAdjustmentDialogValue) => {
    const value = await openLAKAdjustmentDialog({
      userID: props.personID!,
      creditStart: lakIntervallResult.creditStartDate as unknown as Date,
      adjustment,
    });
    await guardDialog(
      async () => {
        await LakAPI.saveAdjustment(props.personID, new LAKAdjustment(value))();
        setForceRecalculate(forceRecalculate + 1);
      },
      { successText: 'Abstimmung gespeichert' },
    );
  };

  const deleteAdjustment = async (adjustment: LAKAdjustmentDialogValue) => {
    if (
      !(await confirmDialog(`Abstimmung „${adjustment.name}“ löschen?`, {
        highlightDecline: true,
        acceptText: 'Löschen',
        declineText: 'Abbrechen',
      }))
    )
      return;
    await guardDialog(
      async () => {
        await LakAPI.deleteAdjustment(props.personID, adjustment._id)();
        setForceRecalculate(forceRecalculate + 1);
      },
      { successText: 'Abstimmung gelöscht' },
    );
  };

  const intervalls = lakIntervallResult?.intervalls ?? [];
  const hasProjected = intervalls.some(i => i.projectedWorkingHoursInIntervall != null);
  const totalActual = roundHours(intervalls.reduce((sum, i) => sum + (i.workingHoursInIntervall ?? 0), 0));
  const totalProjected = roundHours(
    intervalls.reduce((sum, i) => sum + (i.projectedWorkingHoursInIntervall ?? i.workingHoursInIntervall ?? 0), 0),
  );

  const currentYear = new Date().getFullYear();
  const yearRows = collectYearRows(intervalls, previousLAKHours ?? 0);
  /**
   * Everything from this year on is only projected, so it gets a heading of its own.
   */
  const firstFutureYear = yearRows.find(row => row.year > currentYear)?.year;

  const toggleYear = (year: number) =>
    setOpenYears(openYears.includes(year) ? openYears.filter(open => open !== year) : [...openYears, year]);

  /**
   * An Abstimmung explains itself by its name and by how its hours come about - weeks times the hours per week, or
   * the one booking - so the figure on the right can be followed without opening anything.
   */
  const adjustmentSegment = (segment: LAKYearSegment, adjustment: LAKAdjustmentDialogValue) => {
    const isProjected = segment.projectedWorkingHours != null;
    const hours = isProjected ? segment.projectedWorkingHours : segment.workingHours;
    const weeks = isProjected ? segment.projectedCreditedWeeks : segment.creditedWeeks;

    return html`
      <div class="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 py-2">
        ${
          adjustment.end
            ? html`
                <span class="badge badge-accent badge-outline gap-1">
                  <span class="icon-[material-symbols--tune] text-sm"></span>
                  <span class="hidden xs:inline">Abstimmung</span>
                </span>
              `
            : html`
                <span class="badge badge-accent badge-outline gap-1">
                  <span class="icon-[material-symbols--payments] text-sm"></span>
                  <span class="hidden xs:inline">Einmalig</span>
                </span>
              `
        }

        <div class="min-w-0">
          <div class="flex items-center gap-1 font-medium">
            <span class="truncate">${adjustment.name}</span>
            ${
              canEditLak
                ? html`
                    <button
                      class="btn btn-ghost btn-xs btn-square"
                      title="Abstimmung ändern"
                      @click=${() => editAdjustment(adjustment)}
                    >
                      <span class="icon-[material-symbols--edit] text-sm"></span>
                    </button>
                    <button
                      class="btn btn-ghost btn-xs btn-square"
                      title="Abstimmung löschen"
                      @click=${() => deleteAdjustment(adjustment)}
                    >
                      <span class="icon-[material-symbols--delete] text-sm"></span>
                    </button>
                  `
                : nothing
            }
          </div>
          <div class="text-sm text-base-content/70">
            ${
              adjustment.end
                ? html`
                    ${formatDate(adjustment.start as unknown as Date)} –
                    ${formatDate(adjustment.end as unknown as Date)} · ${weeks ?? 0} Wochen à
                    ${formatHours(adjustment.hoursPerWeek)} Std.
                  `
                : html`Einmalig am ${formatDate(adjustment.start as unknown as Date)}`
            }
          </div>
        </div>

        <div
          class="text-right font-semibold ${(hours ?? 0) < 0 ? 'text-error' : isProjected ? 'text-primary' : 'text-success'}"
        >
          ${formatSignedHours(hours)} Std.
        </div>
      </div>
    `;
  };

  const yearSegment = (segment: LAKYearSegment) => {
    if (segment.adjustment) return adjustmentSegment(segment, segment.adjustment);
    const isProjected = segment.projectedWorkingHours != null;
    const creditedWeeks = segment.creditedWeeks ?? 0;
    const weeksWithoutCredit = segment.weeksWithoutCredit ?? [];
    /**
     * Nothing of this segment is over yet, so the projection is all it has. Putting the earned zero
     * next to it would read as if those weeks had been lost rather than not having happened.
     */
    const isFutureOnly = isProjected && creditedWeeks === 0;

    return html`
      <div class="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 py-2">
        ${
          segment.supplementID
            ? html`
                <span class="badge badge-info badge-outline gap-1">
                  <span class="icon-[material-symbols--add-notes] text-sm"></span>
                  <span class="hidden xs:inline">Nachtrag</span>
                </span>
              `
            : html`
                <span class="badge badge-primary badge-outline gap-1">
                  <span class="icon-[material-symbols--contract] text-sm"></span>
                  <span class="hidden xs:inline">Vertrag</span>
                </span>
              `
        }

        <div class="min-w-0">
          <div class="font-medium">
            ${formatDate(segment.start as unknown as Date)} – ${formatDate(segment.end as unknown as Date)}
          </div>
          <div class="text-sm text-base-content/70">
            ${
              isFutureOnly
                ? html`<span class="text-primary">${segment.projectedCreditedWeeks ?? 0} Wochen</span>`
                : html`${creditedWeeks} Wochen`
            }
            à ${formatHours(segment.lakHoursPerWeek)} Std.
            ${
              weeksWithoutCredit.length > 0
                ? html`<span class="text-warning"> · ${weeksWithoutCredit.length} Wochen ohne Anspruch</span>`
                : nothing
            }
            ${
              isProjected && !isFutureOnly
                ? html` · ${segment.projectedCreditedWeeks ?? 0} Wochen bis Jahresende`
                : nothing
            }
          </div>
          ${
            weeksWithoutCredit.length > 0
              ? html`
                  <div class="mt-1 flex flex-col gap-0.5 text-sm text-warning">
                    ${repeat(
                      collectAbsenceReasons(weeksWithoutCredit),
                      reason => reason.absenceClass,
                      reason => html`
                        <div class="flex flex-wrap items-baseline gap-x-1">
                          <span class="icon-[material-symbols--event-busy] shrink-0 self-center text-base"></span>
                          <span class="font-medium">
                            ${String(clientTranslator.translate('_class', reason.absenceClass))}
                          </span>
                          <span class="text-base-content/70">
                            ${reason.weeks} ${reason.weeks === 1 ? 'Woche' : 'Wochen'} ·
                            ${reason.periods.map(formatWeekPeriod).join(', ')}
                          </span>
                        </div>
                      `,
                    )}
                  </div>
                `
              : nothing
          }
        </div>

        <div class="text-right">
          ${
            isFutureOnly
              ? html` <div class="font-semibold text-primary">${formatHours(segment.projectedWorkingHours)} Std.</div> `
              : html`
                  <div class="font-semibold text-success">${formatHours(segment.workingHours)} Std.</div>
                  ${
                    isProjected
                      ? html`
                          <div class="text-sm font-semibold text-primary">
                            ${formatHours(segment.projectedWorkingHours)} Std. bis 31.12.
                          </div>
                        `
                      : nothing
                  }
                `
          }
        </div>
      </div>
    `;
  };

  const yearHeading = (row: LAKYearRow) => {
    const isFuture = row.year > currentYear;
    const isCurrent = row.year === currentYear;
    const showsProjection = !isFuture && row.projectedHours !== row.hours;

    return html`
      <div class="grid grid-cols-[3.5rem_1fr_auto] items-center gap-x-3 text-base">
        <span class="font-semibold ${isFuture ? 'text-base-content/70' : ''}">${row.year}</span>

        <span>
          ${
            isFuture
              ? html`<span class="font-semibold text-primary">${formatSignedHours(row.projectedHours)} Std.</span>`
              : html`
                  <span class="font-semibold ${row.hours < 0 ? 'text-error' : 'text-success'}">
                    ${formatSignedHours(row.hours)} Std.
                  </span>
                  ${
                    showsProjection
                      ? html`<span class="text-sm text-primary">
                          ${formatSignedHours(row.projectedHours)} bis 31.12.</span
                        >`
                      : nothing
                  }
                `
          }
        </span>

        <span class="text-right">
          <span class="block text-sm text-base-content/70">
            ${isCurrent ? 'Stand heute' : `Stand 31.12.${row.year}`}
          </span>
          <span class="font-semibold ${isFuture ? 'text-primary' : ''}">
            ${formatHours(isFuture ? row.projectedBalance : row.balance)}
            <span class="text-sm font-normal text-base-content/70">Std.</span>
          </span>
        </span>
      </div>
    `;
  };

  return html`
    <div class="card-container">
      <div class="card card-body">
        <div class="flex items-center justify-between border-b border-base-200 pb-2 mb-3">
          <span class="text-sm text-base-content/70 flex items-center gap-1">
            <span class="icon-[material-symbols--update] text-base"></span>
            ${
              lakIntervallResult?.createdAt
                ? `Berechnet am ${formatDate(lakIntervallResult.createdAt as unknown as Date)}`
                : 'Noch nicht berechnet'
            }
            ${
              lakIntervallResult?.creditStartDate
                ? `· aus den Verträgen ab ${formatDate(lakIntervallResult.creditStartDate as unknown as Date)}`
                : ''
            }
          </span>
          <div class="flex items-center gap-1">
            ${
              canEditLak
                ? html`
                    <button class="btn btn-ghost btn-sm" @click=${() => editAdjustment()}>
                      <span class="icon-[material-symbols--tune] text-lg"></span>
                      Manuelle Abstimmung eintragen
                    </button>
                  `
                : nothing
            }
            <button
              class="btn btn-ghost btn-sm btn-square"
              title="Neu berechnen"
              @click=${() => setForceRecalculate(forceRecalculate + 1)}
            >
              <span class="icon-[material-symbols--refresh] text-lg"></span>
            </button>
          </div>
        </div>
        <div class="stats stats-vertical sm:stats-horizontal w-full">
          ${
            previousLAKHours || canEditLak
              ? html`
                  <div class="stat">
                    <div class="stat-figure text-base-content/40">
                      <span class="icon-[material-symbols--history] text-3xl sm:text-4xl"></span>
                    </div>
                    <div class="stat-title text-sm text-base-content/70">Übertrag</div>
                    <div class="stat-value text-2xl sm:text-4xl">${formatHours(previousLAKHours)}</div>
                    <div class="stat-desc flex items-center gap-1 text-sm text-base-content/70">
                      <span>
                        ${previousLAKHoursYear ? `Stand 31.12.${previousLAKHoursYear}` : 'Vor der Berechnung im Portal'}
                      </span>
                      ${
                        canEditLak
                          ? html`
                              <button
                                class="btn btn-ghost btn-xs btn-square"
                                title="Übernommene LAK-Stunden ändern"
                                @click=${() => editPreviousLAKHours()}
                              >
                                <span class="icon-[material-symbols--edit] text-sm"></span>
                              </button>
                            `
                          : nothing
                      }
                    </div>
                  </div>
                `
              : nothing
          }
          <div class="stat">
            <div class="stat-figure text-success">
              <span class="icon-[material-symbols--work-history] text-3xl sm:text-4xl"></span>
            </div>
            <div class="stat-title text-sm text-base-content/70">LAK-Stunden heute</div>
            <div class="stat-value text-success text-2xl sm:text-4xl">${formatHours(totalActual)}</div>
            <div class="stat-desc text-sm text-base-content/70">Angesammelt bis heute</div>
          </div>
          ${
            hasProjected
              ? html`
                  <div class="stat">
                    <div class="stat-figure text-primary">
                      <span class="icon-[material-symbols--trending-up] text-3xl sm:text-4xl"></span>
                    </div>
                    <div class="stat-title text-sm text-base-content/70">Prognose in der Zukunft</div>
                    <div class="stat-value text-primary text-2xl sm:text-4xl">${formatHours(totalProjected)}</div>
                    <div class="stat-desc text-sm text-base-content/70">
                      Projiziert bis
                      ${
                        lakIntervallResult?.creditEndDate
                          ? `${formatDate(lakIntervallResult.creditEndDate as unknown as Date)} (${
                              lakIntervallResult.creditEndBySchwerbehinderung
                                ? 'Schwerbehinderung'
                                : lakIntervallResult.creditEndFollowsSchulhalbjahr
                                  ? 'Schulhalbjahr des 60. Lebensjahres'
                                  : '60. Lebensjahr'
                            })`
                          : 'zum Vertragsende'
                      }
                    </div>
                  </div>
                `
              : nothing
          }
        </div>
      </div>

      ${
        !lakIntervallResult?.creditEndDate
          ? html`
              <div role="alert" class="alert alert-warning">
                <span class="icon-[material-symbols--warning] text-xl shrink-0"></span>
                <span>
                  Kein Geburtsdatum hinterlegt. Es kann nicht berechnet werden, wann die Gutschrift mit dem 60.
                  Lebensjahr endet.
                </span>
              </div>
            `
          : nothing
      }
      ${
        lakIntervallResult?.creditEndBySchwerbehinderung && lakIntervallResult.creditEndDate
          ? html`
              <div role="alert" class="alert alert-info">
                <span class="icon-[material-symbols--info] text-xl shrink-0"></span>
                <span>
                  Schwerbehinderung ab Grad 50 hinterlegt. Die Gutschrift endet am
                  ${formatDate(lakIntervallResult.creditEndDate as unknown as Date)}.
                </span>
              </div>
            `
          : nothing
      }
      ${
        yearRows.length === 0
          ? html`
              <div class="card card-body flex flex-col items-center justify-center gap-4 py-16 text-center">
                <span class="icon-[material-symbols--event-busy] text-5xl text-base-content/30"></span>
                <p class="text-base-content/70">Keine berechneten Jahre gefunden</p>
              </div>
            `
          : html`
              <div class="card card-body">
                <div class="flex items-center justify-between border-b border-base-200 pb-2 mb-1">
                  <h2 class="font-semibold md:text-lg">Jahresabschlüsse</h2>
                  <span class="text-sm text-base-content/70"
                    >Eine Woche zählt zu dem Jahr, in dem ihr Donnerstag liegt</span
                  >
                </div>

                ${
                  previousLAKHours
                    ? html`
                        <div
                          class="grid grid-cols-[3.5rem_1fr_auto] items-center gap-x-3 border-b-2 border-base-300 py-3"
                        >
                          <span class="icon-[material-symbols--history] text-2xl text-base-content/40"></span>
                          <span class="font-semibold">
                            Übertrag
                            <span class="block text-sm font-normal text-base-content/70">
                              Vor der Berechnung im Portal angesammelt
                            </span>
                          </span>
                          <span class="text-right">
                            <span class="block text-sm text-base-content/70">
                              ${previousLAKHoursYear ? `Stand 31.12.${previousLAKHoursYear}` : 'Stand unbekannt'}
                            </span>
                            <span class="font-semibold">
                              ${formatHours(previousLAKHours)}
                              <span class="text-sm font-normal text-base-content/70">Std.</span>
                            </span>
                          </span>
                        </div>
                      `
                    : nothing
                }

                <div class="divide-y divide-base-200">
                  ${repeat(
                    yearRows,
                    row => row.year,
                    row => html`
                      ${
                        row.year === firstFutureYear
                          ? html`
                              <div
                                class="flex items-center gap-2 pt-3 pb-1 text-sm uppercase tracking-wide text-primary"
                              >
                                <span class="icon-[material-symbols--trending-up] text-base"></span>
                                Prognose
                              </div>
                            `
                          : nothing
                      }
                      ${Collapse({
                        heading: yearHeading(row),
                        content: html`
                          <div class="pt-1">
                            <div class="divide-y divide-base-200 pl-9">
                              ${repeat(
                                row.segments,
                                segment => `${segment.contractID}_${segment.supplementID}_${segment.adjustment?._id}`,
                                segment => yearSegment(segment),
                              )}
                            </div>
                          </div>
                        `,
                        open: openYears.includes(row.year),
                        onToggle: () => toggleYear(row.year),
                        className: 'py-2 border-0 bg-transparent shadow-none',
                      })}
                    `,
                  )}
                </div>
              </div>
            `
      }
    </div>
  `;
});
